import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BusinessRelationLifecycle,
  BusinessRelationStatus,
  BusinessRelationType,
} from 'pco-product-service-internal-consumer';
import { getBrType } from 'services/partner/getBrType';

export type BusinessRelationTypeGateStatus = 'idle' | 'checking' | 'intermediary' | 'private' | 'failed';

type BusinessRelationTypeGate = {
  brLifecycle: BusinessRelationLifecycle | undefined;
  brStatus: BusinessRelationStatus | undefined;
  canLoadAccountData: boolean;
  error: Error | null;
  isActiveBr: boolean;
  isBrTypeUnavailable: boolean;
  isPrivateBankingBr: boolean;
  isWaitingForCurrentBrType: boolean;
  status: BusinessRelationTypeGateStatus;
};

export const businessRelationTypeQueryKey = (brUid: string | undefined) => ['businessRelationType', brUid] as const;

export const useBusinessRelationTypeGate = (resolvedBrUid: string | undefined): BusinessRelationTypeGate => {
  const { data, error, isError } = useQuery({
    queryKey: businessRelationTypeQueryKey(resolvedBrUid),
    queryFn: () => {
      if (!resolvedBrUid) {
        throw new Error('brUid is required');
      }

      return getBrType(resolvedBrUid);
    },
    enabled: !!resolvedBrUid,
    retry: false,
  });

  return useMemo(() => {
    const status: BusinessRelationTypeGateStatus = (() => {
      if (!resolvedBrUid) {
        return 'idle';
      }

      if (isError) {
        return 'failed';
      }

      if (data?.brType === BusinessRelationType.PrivateBanking) {
        return 'private';
      }

      if (data?.brType) {
        return 'intermediary';
      }

      return 'checking';
    })();

    const isWaitingForCurrentBrType = status === 'checking';
    const isPrivateBankingBr = status === 'private';

    return {
      brLifecycle: data?.lifecycle,
      brStatus: data?.status,
      canLoadAccountData: !isWaitingForCurrentBrType && !isPrivateBankingBr,
      error,
      isActiveBr:
        data?.status === BusinessRelationStatus.Active && data?.lifecycle === BusinessRelationLifecycle.Operating,
      isBrTypeUnavailable: status === 'failed',
      isPrivateBankingBr,
      isWaitingForCurrentBrType,
      status,
    };
  }, [data, error, isError, resolvedBrUid]);
};
